import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api, apiError, API } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";
import { ArrowClockwise, CheckCircle, Warning, XCircle } from "@phosphor-icons/react";

const GROUPS = [
  "Capital Account", "Reserves & Surplus", "Loans (Liability)", "Current Liabilities", "Sundry Creditors",
  "Duties & Taxes", "Provisions", "Fixed Assets", "Investments", "Current Assets", "Sundry Debtors",
  "Bank Accounts", "Cash-in-Hand", "Stock-in-Hand", "Loans & Advances (Asset)", "Sales Accounts",
  "Direct Income", "Indirect Income", "Purchase Accounts", "Direct Expenses", "Indirect Expenses",
];

export default function Migration() {
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState(null);
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState("");
  const [source, setSource] = useState("auto");
  const fileRef = useRef(null);

  const load = () => api.get("/migration/batches").then((r) => setBatches(r.data));
  useEffect(() => { load(); }, []);

  const openBatch = async (id) => {
    const { data } = await api.get(`/migration/batches/${id}`);
    setBatch(data);
    setDry(data.dry_run || null);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      const { data } = await api.post("/migration/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Staged ${data.counts.ledgers} ledgers, ${data.counts.items} items, ${data.counts.vouchers} vouchers`);
      setBatch(data);
      setDry(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const runDry = async () => {
    setBusy("dry");
    try {
      const { data } = await api.post(`/migration/batches/${batch.id}/dry-run`);
      setDry(data);
      toast.success(data.ready ? "Dry run reconciled — ready to commit" : "Dry run finished with warnings");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const commit = async () => {
    setBusy("commit");
    try {
      const { data } = await api.post(`/migration/batches/${batch.id}/commit`);
      toast.success(`Committed: ${data.created.ledgers} ledgers, ${data.created.items} items, ${data.created.vouchers} vouchers`);
      await openBatch(batch.id);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const rollback = async () => {
    setBusy("rollback");
    try {
      const { data } = await api.post(`/migration/batches/${batch.id}/rollback`);
      toast.success(`Rolled back ${Object.values(data.deleted).reduce((a, b) => a + b, 0)} records`);
      await openBatch(batch.id);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const remap = async (name, group) => {
    try {
      await api.post(`/migration/batches/${batch.id}/remap`, { ledger_name: name, mapped_group: group });
      await openBatch(batch.id);
      setDry(null);
      toast.success(`${name} → ${group}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="migration-page">
      <PageHeader title="Migration Wizard" subtitle="Tally XML, Excel or CSV → AI-mapped staging → dry-run reconciliation → commit with 30-day rollback.">
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="migration-source-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Detect from file</SelectItem>
            <SelectItem value="tally">Tally XML</SelectItem>
            <SelectItem value="excel">Excel / CSV</SelectItem>
          </SelectContent>
        </Select>
        <a href={`${API}/migration/template.csv`} className="text-xs text-primary underline" data-testid="download-template-link">
          CSV template
        </a>
        <input ref={fileRef} type="file" accept=".xml,.csv,.xlsx,.xlsm" onChange={upload} className="hidden" data-testid="migration-file-input" />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy === "upload"} data-testid="migration-upload-btn">
          {busy === "upload" ? "Parsing…" : "Upload source file"}
        </Button>
      </PageHeader>

      {batches.length === 0 && !batch ? (
        <Empty label="No migration batches" hint="Upload a Tally XML export, an Excel workbook (sheets: Ledgers / Items / Vouchers) or a CSV." />
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="dense-table w-full min-w-[760px]" data-testid="migration-batches-table">
            <thead><tr><th>Uploaded</th><th>Source</th><th>File</th><th className="text-right">Ledgers</th>
              <th className="text-right">Items</th><th className="text-right">Vouchers</th>
              <th className="text-right">Conflicts</th><th>Status</th></tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="cursor-pointer row-in" onClick={() => openBatch(b.id)} data-testid={`batch-row-${b.id}`}>
                  <td className="num whitespace-nowrap">{fmtDate(b.created_at)}</td>
                  <td>{b.source}</td>
                  <td className="max-w-[220px] truncate text-muted-foreground">{b.filename}</td>
                  <td className="num text-right">{b.counts?.ledgers ?? 0}</td>
                  <td className="num text-right">{b.counts?.items ?? 0}</td>
                  <td className="num text-right">{b.counts?.vouchers ?? 0}</td>
                  <td className="num text-right">{b.counts?.conflicts ?? 0}</td>
                  <td>
                    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${
                      b.status === "committed" ? "bg-success/10 text-success" :
                      b.status === "rolled-back" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
                      {b.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!batch} onOpenChange={(o) => !o && setBatch(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl" data-testid="batch-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">
              {batch?.source} · {batch?.filename}
              <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">{batch?.status}</span>
            </SheetTitle>
          </SheetHeader>
          {batch ? (
            <div className="mt-4 space-y-5 text-sm">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={runDry} disabled={busy === "dry" || batch.status === "committed"} data-testid="dry-run-btn">
                  {busy === "dry" ? "Reconciling…" : "Run dry-run reconciliation"}
                </Button>
                <Button size="sm" onClick={commit} disabled={busy === "commit" || !dry || batch.status === "committed"} data-testid="commit-btn">
                  {busy === "commit" ? "Committing…" : "Commit migration"}
                </Button>
                {batch.status === "committed" && (
                  <Button size="sm" variant="outline" onClick={rollback} disabled={busy === "rollback"}
                    className="gap-1 text-destructive" data-testid="rollback-btn">
                    <ArrowClockwise size={13} /> {busy === "rollback" ? "Reverting…" : "Rollback"}
                  </Button>
                )}
              </div>

              {dry ? (
                <div className="space-y-3" data-testid="dry-run-report">
                  <div className="grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-4">
                    {[["Ledgers", dry.will_create.ledgers], ["Items", dry.will_create.items],
                      ["Vouchers", dry.will_create.vouchers], ["Held back", dry.will_create.held_vouchers]].map(([k, v]) => (
                      <div key={k} className="bg-background p-3">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                        <p className="num mt-1 font-semibold">{v}</p>
                      </div>
                    ))}
                  </div>
                  <table className="dense-table w-full">
                    <thead><tr><th>Reconciliation check</th><th className="text-right">Expected</th>
                      <th className="text-right">Actual</th><th className="text-right">Diff</th><th /></tr></thead>
                    <tbody>
                      {dry.checks.map((c) => (
                        <tr key={c.check} data-testid={`check-${c.check.slice(0, 14).replace(/\s/g, "-")}`}>
                          <td>
                            <p className="font-medium">{c.check}</p>
                            <p className="text-[11px] text-muted-foreground">{c.hint}</p>
                          </td>
                          <td className="num text-right">{money(c.expected)}</td>
                          <td className="num text-right">{money(c.actual)}</td>
                          <td className="num text-right">{money(c.difference)}</td>
                          <td className="text-right">
                            {c.passed ? <CheckCircle size={15} className="text-success" /> : <XCircle size={15} className="text-destructive" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dry.party_reconciliation.length > 0 && (
                    <table className="dense-table w-full">
                      <thead><tr><th>Party balance</th><th className="text-right">Current</th>
                        <th className="text-right">Incoming</th><th className="text-right">After</th><th>Note</th></tr></thead>
                      <tbody>
                        {dry.party_reconciliation.map((p) => (
                          <tr key={p.name}>
                            <td className="font-medium">{p.name}</td>
                            <td className="num text-right">{money(p.current)}</td>
                            <td className="num text-right">{money(p.incoming)}</td>
                            <td className="num text-right font-medium">{money(p.after)}</td>
                            <td className="text-[11px] text-muted-foreground">{p.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="flex gap-px border border-border bg-border">
                    {[["Stock value before", dry.before.stock_value], ["Trial balance Dr", dry.before.trial_balance_dr],
                      ["Trial balance Cr", dry.before.trial_balance_cr]].map(([k, v]) => (
                      <div key={k} className="flex-1 bg-background p-3">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                        <p className="num mt-1">{money(v)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {(batch.conflicts || []).length > 0 ? (
                <div className="border border-border" data-testid="conflicts-panel">
                  <p className="border-b border-border px-3 py-2 font-display text-sm font-bold">
                    Conflicts ({batch.conflicts.length})
                  </p>
                  <div className="divide-y divide-border">
                    {batch.conflicts.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-2 text-xs">
                        <Warning size={13} className="mt-0.5 shrink-0 text-chart-4" />
                        <span><span className="font-medium">{c.name}</span> — {c.detail}
                          <span className="ml-1 text-[10px] uppercase tracking-widest text-muted-foreground">{c.kind}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-border px-3 py-2 text-xs text-muted-foreground" data-testid="conflicts-panel">
                  No conflicts detected in this batch.
                </div>
              )}

              <div className="border border-border">
                <p className="border-b border-border px-3 py-2 font-display text-sm font-bold">Ledger mapping</p>
                <table className="dense-table w-full">
                  <thead><tr><th>Source name</th><th>Source group</th><th>Mapped group</th>
                    <th className="text-right">Opening</th><th className="text-right">Conf.</th></tr></thead>
                  <tbody>
                    {(batch.ledgers || []).map((l) => (
                      <tr key={l.name} data-testid={`map-row-${l.name.replace(/\s/g, "-")}`}>
                        <td className="font-medium">{l.name}</td>
                        <td className="text-muted-foreground">{l.source_group || "—"}</td>
                        <td>
                          {batch.status === "staged" || batch.status === "dry-run" ? (
                            <Select value={l.mapped_group} onValueChange={(v) => remap(l.name, v)}>
                              <SelectTrigger className="h-7 border-0 text-xs" data-testid={`remap-select-${l.name.replace(/\s/g, "-")}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : l.mapped_group}
                        </td>
                        <td className="num text-right">{money(l.opening)} {l.opening_dc}</td>
                        <td className={`num text-right ${l.map_confidence < 0.6 ? "text-chart-4" : "text-muted-foreground"}`}>
                          {Math.round((l.map_confidence || 0) * 100)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-border">
                <p className="border-b border-border px-3 py-2 font-display text-sm font-bold">Vouchers staged</p>
                <table className="dense-table w-full">
                  <thead><tr><th>Source no.</th><th>Type</th><th>Date</th><th className="text-right">Dr</th>
                    <th className="text-right">Cr</th><th>Action</th></tr></thead>
                  <tbody>
                    {(batch.vouchers || []).map((v, i) => (
                      <tr key={i}>
                        <td className="num">{v.number || "—"}</td>
                        <td>{v.type}</td>
                        <td className="num">{v.date}</td>
                        <td className="num text-right">{money(v.total_dr)}</td>
                        <td className="num text-right">{money(v.total_cr)}</td>
                        <td className={v.action === "create" ? "text-success" : "text-destructive"}>
                          {v.action === "create" ? "will import" : `held · ${v.unknown_ledgers?.join(", ") || "unbalanced"}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(batch.items || []).length > 0 && (
                <div className="border border-border">
                  <p className="border-b border-border px-3 py-2 font-display text-sm font-bold">Stock items staged</p>
                  <table className="dense-table w-full">
                    <thead><tr><th>Item</th><th>HSN</th><th>UOM</th><th className="text-right">GST</th>
                      <th className="text-right">Opening qty</th><th className="text-right">Rate</th></tr></thead>
                    <tbody>
                      {batch.items.map((it) => (
                        <tr key={it.name}>
                          <td className="font-medium">{it.name}</td>
                          <td className="num">{it.hsn || "—"}</td>
                          <td>{it.uom}</td>
                          <td className="num text-right">{it.gst_rate}%</td>
                          <td className="num text-right">{it.opening_qty}</td>
                          <td className="num text-right">{money(it.rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
